import React from "react";
import usePagedList from "../../hooks/usePagedList";
import Pager from "../../components/ui/Pager";
import LoadFailure from "../../components/ui/LoadFailure";
import ReviewCard from "./ReviewCard";
import { REVIEW_PATHS } from "../contract";
import { reviewErrorText } from "../errorText";
import { REVIEWS } from "../../constants/testIds/reviews";

/**
 * `Pager` appends `-prev` / `-next` / `-info`, and the contract names the first two
 * verbatim (`reviews-pager-prev`, `reviews-pager-next`). Derived from the registry rather
 * than re-spelled, so a rename of either id is a compile-time-visible change here and not
 * a browser test that silently stops finding a button.
 */
const PAGER_PREFIX = REVIEWS.reviewsPagerNext.replace(/-next$/, "");

/**
 * The practice's published reviews, one page at a time.
 *
 * ITS OWN READ, DELIBERATELY. The profile page loads its header/services/summary through
 * `usePractitionerProfile` and its reviews through this component, so a failure of one
 * does not blank the other: a visitor whose review page 404s still sees the practice and
 * can still book, and a visitor whose profile read fails is not also told the practice has
 * no reviews. Two facts, two reads, two failure states.
 *
 * A FAILED READ IS NOT AN EMPTY LIST (BUG-051). `usePagedList` holds the failure as a
 * first-class state; `loaded && rows.length === 0` is the only thing that may say "no
 * reviews yet", and `loadError` says so instead when the ask itself failed.
 *
 * `t` and `lang` come from the page — this component stays presentational apart from its
 * own read, and its strings are the `marketplace` namespace's (`ReviewCard`'s note).
 *
 * @param {string} slug the practitioner's marketplace slug
 * @param {function} t `useT("marketplace")`
 * @param {function} tv `useT("common")` — the keys `extractApiError` owns
 * @param {string} lang
 * @param {"dark"|"light"} [variant]
 */
export default function ReviewList({ slug, t, tv, lang, variant = "dark" }) {
  const list = usePagedList(REVIEW_PATHS.practitionerReviews(slug));
  const muted = variant === "dark" ? "text-white/50" : "text-eh-muted";

  if (list.loadError) {
    return (
      <LoadFailure
        message={reviewErrorText(list.loadError, { t, tv, fallback: tv("errors.loadFailed") })}
        onRetry={list.reload}
        retryLabel={tv("actions.retry")}
        testId={REVIEWS.reviewsLoadError}
        retryTestId="reviews-list-retry"
      />
    );
  }

  if (list.loaded && list.rows.length === 0) {
    return (
      <p className={`text-sm text-center py-10 ${muted}`} data-testid={REVIEWS.reviewsEmpty}>
        {t("profile.reviewsEmpty")}
      </p>
    );
  }

  return (
    <div data-testid={REVIEWS.reviewsList}>
      <ul className="m-0 p-0 space-y-4">
        {list.rows.map((r) => (
          <ReviewCard key={r.id} review={r} t={t} lang={lang} variant={variant} />
        ))}
      </ul>
      {/* The pager's own test ids are fixed by the contract rather than derived from a
          `testId` prefix, so the browser journey can name them without knowing this
          component's internal naming. `Pager` builds `${testId}-prev|next|info`, so the
          prefix is chosen to produce exactly the contract's spellings. */}
      <Pager
        page={list.page}
        pageSize={list.pageSize}
        total={list.total}
        shown={list.rows.length}
        onPage={list.setPage}
        infoText={(vars) => t("profile.page.info", vars)}
        prevLabel={t("profile.page.prev")}
        nextLabel={t("profile.page.next")}
        testId={PAGER_PREFIX}
        tone={variant === "dark" ? "dark" : "admin"}
      />
    </div>
  );
}
